import { VatMode, grossPriceFromNet, netPriceFromGross } from '@crm/shared'
import type { RequestItem } from '../../../shared/api/types'
import type { SelectedPrice } from '../hooks/useKpMode'

type KpSourceItem = Pick<
  RequestItem,
  'id' | 'partNumber' | 'customerPartNumber' | 'brand' | 'quantity' | 'unit' | 'kpNote'
>

export type KpItemDraft = {
  itemId: string
  partNumber: string
  customerPartNumber: string
  brand: string | null
  quantity: number
  unit: string | null
  cost: number
  matrix: string
  markup: number
  priceWithMarkup: number
  /** Цена без НДС до копейки — только в КП «НДС сверху». */
  priceNet?: number
  supplierName: string | null
  note: string | null // «Примечание к КП» из позиции (item.kpNote)
}

export function buildKpItems(params: {
  items: KpSourceItem[]
  selectedPrices: Map<string, SelectedPrice>
  kpMarkups: Record<string, number>
  positionOverrides: Record<string, number>
  /** «НДС сверху»: цена без НДС = цена с наценкой / 1,22 до копейки, цена с НДС — из неё до 4 знаков. */
  vatMode?: VatMode
}): KpItemDraft[] {
  const { items, selectedPrices, kpMarkups, positionOverrides, vatMode } = params
  const onTop = vatMode === VatMode.ON_TOP

  return items
    .map((item): KpItemDraft | null => {
      const selected = selectedPrices.get(item.id)
      if (!selected) return null

      const markup = positionOverrides[item.id] ?? kpMarkups[selected.matrix] ?? 1
      const priceWithMarkup = Math.round(selected.cost * markup * 100) / 100
      const priceNet = onTop ? netPriceFromGross(priceWithMarkup) : undefined

      return {
        itemId: item.id,
        partNumber: item.partNumber,
        customerPartNumber: item.customerPartNumber ?? item.partNumber,
        brand: item.brand ?? null,
        quantity: item.quantity,
        unit: item.unit ?? null,
        cost: selected.cost,
        matrix: selected.matrix,
        markup,
        priceWithMarkup: priceNet !== undefined ? grossPriceFromNet(priceNet) : priceWithMarkup,
        ...(priceNet !== undefined ? { priceNet } : {}),
        supplierName: selected.supplierName,
        note: item.kpNote ?? null,
      }
    })
    .filter((item): item is KpItemDraft => item !== null)
}
